/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { C, t, fmt$, formatDate } from "../shared/globals.js";

function BuyZoneHeatmap({ entries, currentPrice }) {
  const [hovered, setHovered] = useState(null);
  const wrapRef = useRef(null);
  const [w, setW] = useState(600);

  useEffect(() => {
    const obs = new ResizeObserver(es => setW(es[0].contentRect.width || 600));
    if (wrapRef.current) {
      obs.observe(wrapRef.current);
      requestAnimationFrame(() => {
        if (wrapRef.current) {
          const { width } = wrapRef.current.getBoundingClientRect();
          if (width > 0) setW(width);
        }
      });
    }
    return () => obs.disconnect();
  }, []);

  const BUCKET = 20; // $20 price buckets
  const fmt$ = v => `$${Math.round(v).toLocaleString()}`;
  const fmt$full = v => new Intl.NumberFormat("en-US",{style:"currency",currency:"USD",minimumFractionDigits:0}).format(v);

  // Group entries into price buckets
  const bucketMap = {};
  entries.forEach(e => {
    const buyPrice = e.shares > 0 ? e.dollars / e.shares : 0;
    if (!buyPrice) return;
    const bucket = Math.floor(buyPrice / BUCKET) * BUCKET;
    if (!bucketMap[bucket]) bucketMap[bucket] = { dollars:0, shares:0, count:0 };
    bucketMap[bucket].dollars += e.dollars;
    bucketMap[bucket].shares  += e.shares;
    bucketMap[bucket].count++;
  });

  const buckets = Object.entries(bucketMap)
    .map(([k, v]) => ({ price: parseInt(k), ...v }))
    .sort((a, b) => b.price - a.price); // high to low

  if (!buckets.length) return null;

  const maxShares  = Math.max(...buckets.map(b => b.shares));
  const maxDollars = Math.max(...buckets.map(b => b.dollars));

  const BAR_H = 10, GAP = 3;
  const PAD = { t:4, r:16, b:4, l:72 };
  const H = buckets.length * (BAR_H + GAP) + PAD.t + PAD.b;

  return (
    <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:10, padding:"16px 20px", height:"100%", boxSizing:"border-box" }}>
      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
        letterSpacing:"0.3em", color:"var(--text4)", marginBottom:14 }}>
        BUY ZONE HEATMAP
      </div>
      <div ref={wrapRef} style={{ position:"relative" }}>
        <svg width="100%" height={H} viewBox={`0 0 ${w} ${H}`} style={{ display:"block" }}>
          {buckets.map((b, i) => {
            const y        = PAD.t + i * (BAR_H + GAP);
            const isAbove  = b.price >= currentPrice;
            const isCurrent = b.price <= currentPrice && b.price + BUCKET > currentPrice;
            const sharesFrac  = maxShares  > 0 ? b.shares  / maxShares  : 0;
            const dollarsFrac = maxDollars > 0 ? b.dollars / maxDollars : 0;
            const barColor = isAbove ? `rgba(224,62,26,${0.3 + sharesFrac*0.55})`
                                     : `rgba(90,173,39,${0.3 + sharesFrac*0.55})`;
            const barW = Math.max(4, (w - PAD.l - PAD.r) * sharesFrac);
            const isHov = hovered === i;

            return (
              <g key={b.price}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}>
                {/* Price label */}
                <text x={PAD.l - 6} y={y + BAR_H/2 + 4} textAnchor="end"
                  fill={isCurrent ? C.orange : isHov ? "var(--text)" : "var(--text4)"}
                  fontSize="8" fontFamily="Montserrat,sans-serif" fontWeight="700">
                  {fmt$(b.price)}
                </text>
                {/* Track */}
                <rect x={PAD.l} y={y} width={w - PAD.l - PAD.r} height={BAR_H}
                  rx="4" fill="var(--bg3)"/>
                {/* Bar */}
                <rect x={PAD.l} y={y} width={barW} height={BAR_H}
                  rx="4" fill={barColor} style={{ transition:"fill 0.15s" }}/>
                {/* Current price marker */}
                {isCurrent && (
                  <rect x={PAD.l} y={y} width={3} height={BAR_H} fill={C.orange} rx="2"/>
                )}
                {/* Shares label inside bar */}
                {isHov && (
                  <text x={PAD.l + barW + 6} y={y + BAR_H/2 + 4}
                    fill="var(--text3)" fontSize="9" fontFamily="Montserrat,sans-serif" fontWeight="700">
                    {b.shares.toFixed(3)} sh · {fmt$full(b.dollars)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
        {/* Legend */}
        <div style={{ display:"flex", gap:14, marginTop:10 }}>
          {[{color:"rgba(90,173,39,0.7)", label:"BELOW CURRENT"},{color:"rgba(224,62,26,0.7)",label:"ABOVE CURRENT"}].map(({color,label})=>(
            <div key={label} style={{ display:"flex", alignItems:"center", gap:5 }}>
              <div style={{ width:10, height:10, borderRadius:2, background:color }}/>
              <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, color:"var(--text4)", letterSpacing:"0.1em" }}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default BuyZoneHeatmap;
